import { useMemo, useState } from 'react';
import SectionButton from 'lib/SectionButton';
import { SectionButtonTheme, SectionSelectedType } from 'lib/SectionButton/SectionButton.types';
import Chip from 'lib/Chip';
import { formatCurrency, formatNumber } from 'modules/I18N';
import locationSvg from 'assets/icons/location.svg';
import formatsSvg from 'assets/icons/formats.svg';
import tagSvg from 'assets/icons/tag.svg';
import routeFrameListSvg from 'assets/icons/route_frame_lists.svg';
import moneySvg from 'assets/icons/money.svg';
import framesSvg from 'assets/icons/frame.svg';
import eyeSvg from 'assets/icons/eye_empty.svg';
import poisSvg from 'assets/icons/pois.svg';
import { useSelector } from 'react-redux';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import { Store } from 'components/common/types/Store.types';
import { Tag } from 'components/common/types/Deal.types';
import { isEmpty } from 'lodash';
import {
  isOneFilterSelected,
  isOneTargetSelected,
  isOnlyOneFilterSelected,
  isOnlyOneTargetSelected,
} from 'utils/objectives';
import { TargetLabel } from 'consts/targetLabel';
import { getChipColor } from 'utils/getChipColor';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import { formatLineDataToAllocation } from 'utils/formatLineDataToAllocation';
import { countIncludedAndExcludedPOI, groupPOIByParentCategory } from 'utils/poiUtils';
import { getIsProgrammaticEndedAddNewLineEnabled } from 'store/dealManagement/selectors/getIsProgrammaticEndedAddNewLineEnabled';
import { sortCategories } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import { ORDERED_DSM_LOCATION_CATEGORY } from '../../FiltersContent/Location/utils';
import { sortByCategoryIncludeExclude } from '../../FiltersContent/utils';
import { formatTagsToCategory } from '../../FiltersContent/Tags/utils';
import { ObjectiveSelectionProps } from './ObjectiveSelection.types';
import { generateRouteFramesSelectedDisplay } from '../../FiltersContent/FrameList/utils/generateRouteFramesSelectedDisplay';
import { POIChipList } from '../../DataTargetingContent/PointsOfInterest/POIChipList';
import { ObjectiveSelectionMenu } from '../ObjectiveSelectionMenu/ObjectiveSelectionMenu';
import { useObjectiveSelection } from './useObjectiveSelection';

const ObjectiveSelection: React.FC<ObjectiveSelectionProps> = ({
  lineId,
  objectives,
  objective,
  onClick,
  areEllipsisMenusDisabled = false,
  lineAllocation = {},
  onShowMoreMenuClick,
  onEditObjective,
  onExplode,
  isClearAllDisabled = false,
  isDisabled = false,
  isProductObjective = false,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { objectiveGroupId, budget, frames, impressions, locations, formats, tags, frameList, poi } = objective;

  const {
    onRemoveLocationChip,
    onRemoveFormatChip,
    onRemoveTagChip,
    onRemoveFrameListChip,
    onRemovePOIChip,
    clearLocations,
    clearFormats,
    clearTags,
    clearFrameList,
    clearPOI,
    clearBudget,
    clearFrames,
    clearImpressions,
  } = useObjectiveSelection({ lineId, objectives, objective, onEditObjective, isProductObjective });

  const { listFiles, routeFrameCodes, mapRouteFrameCodes } = frameList;
  const { localeCode, currencyCode } = useSelector((state: Store) => state.publisher.configuration);
  const availableTags = useSelector((state: Store) => state.userOptions.referenceOptions.tags);
  const isProgrammaticEndedAddNewLineEnabled = useSelector(getIsProgrammaticEndedAddNewLineEnabled);
  const { isLineReadOnly } = useIsReadOnly({ isEditingAllowed: isProgrammaticEndedAddNewLineEnabled });

  const [isCollapsed, setIsCollapsed] = useState({
    locations: false,
    formats: false,
    poi: false,
    tags: false,
    frameList: false,
  });

  const budgetAllocation = formatLineDataToAllocation(lineAllocation, budget?.objectiveId ?? null);
  const framesAllocation = formatLineDataToAllocation(lineAllocation, frames?.objectiveId ?? null);
  const impressionsAllocation = formatLineDataToAllocation(lineAllocation, impressions?.objectiveId ?? null);

  const categorizedLocations = useMemo(() => {
    return sortCategories(sortByCategoryIncludeExclude(locations), ORDERED_DSM_LOCATION_CATEGORY);
  }, [locations]);

  const categorizedFormats = useMemo(() => {
    return sortByCategoryIncludeExclude(formats);
  }, [formats]);

  const loadTagsCategories = (selected: Tag[]): SectionSelectedType => {
    const tagsWithCategory = !isEmpty(availableTags) ? formatTagsToCategory(selected, availableTags) : selected;

    return sortByCategoryIncludeExclude(tagsWithCategory);
  };

  const categorizedTags = useMemo(() => {
    return loadTagsCategories(tags);
  }, [availableTags, tags]);

  const categorizedPoi = useMemo(() => groupPOIByParentCategory(poi), [poi]);

  const { included: collapsedIncludedPoiNumber, excluded: collapsedExcludedPoiNumber } = useMemo(
    () => countIncludedAndExcludedPOI(poi),
    [poi],
  );

  const generatedSelectedAndUploaded = useMemo(() => {
    return generateRouteFramesSelectedDisplay(routeFrameCodes, mapRouteFrameCodes ?? [], listFiles);
  }, [listFiles, routeFrameCodes, mapRouteFrameCodes]);

  const selectedView = useMemo(() => {
    return sortByCategoryIncludeExclude(generatedSelectedAndUploaded);
  }, [generatedSelectedAndUploaded]);

  const isTargetSelected = useMemo(() => isOneTargetSelected(objective), [objective]);
  const isFilterSelected = useMemo(() => isOneFilterSelected(objective), [objective]);
  const isSingleTargetSelected = useMemo(() => isOnlyOneTargetSelected(objective), [objective]);
  const isSingleFilterSelected = useMemo(() => isOnlyOneFilterSelected(objective), [objective]);

  const getSubLabel = (value?: number): string | undefined => {
    return value !== undefined
      ? formatNumber({
          value,
          localeCode,
        })
      : undefined;
  };

  if (!isTargetSelected && !isFilterSelected) return null;

  return (
    <div data-test-id={`objective-${objectiveGroupId}-selection`} className="flex flex-col gap-1 my-1">
      {locations.length ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-locations`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          icon={locationSvg}
          label="Location"
          theme={SectionButtonTheme.SOLID}
          selected={categorizedLocations}
          onClick={onClick}
          onRemoveChip={onRemoveLocationChip}
          areChipsAutoCollapsed={isCollapsed.locations}
          isRemoveChipDisabled={isSingleFilterSelected}
          customMenuContent={
            <ObjectiveSelectionMenu
              isDisabled={isDisabled || isLineReadOnly || areEllipsisMenusDisabled}
              isCollapsed={isCollapsed.locations}
              isCollapseDisabled={locations.length === 1}
              onClear={clearLocations}
              onCollapseOrExpand={() => setIsCollapsed({ ...isCollapsed, locations: !isCollapsed.locations })}
              onExplode={() => {
                onExplode?.(objective, 'locations');
                setIsCollapsed({ ...isCollapsed, locations: false });
              }}
              isExplodeDisabled={locations.length === 1}
              isClearAllDisabled={isClearAllDisabled || isSingleFilterSelected}
            />
          }
        />
      ) : null}
      {formats.length ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-formats`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          icon={formatsSvg}
          label="Format"
          theme={SectionButtonTheme.SOLID}
          selected={categorizedFormats}
          onClick={onClick}
          areChipsAutoCollapsed={isCollapsed.formats}
          onRemoveChip={onRemoveFormatChip}
          isRemoveChipDisabled={isSingleFilterSelected}
          customMenuContent={
            <ObjectiveSelectionMenu
              isDisabled={isDisabled || isLineReadOnly || areEllipsisMenusDisabled}
              isCollapsed={isCollapsed.formats}
              isCollapseDisabled={formats.length === 1}
              onClear={clearFormats}
              onCollapseOrExpand={() => setIsCollapsed({ ...isCollapsed, formats: !isCollapsed.formats })}
              onExplode={() => {
                onExplode?.(objective, 'formats');
                setIsCollapsed({ ...isCollapsed, formats: false });
              }}
              isExplodeDisabled={formats.length === 1}
              isClearAllDisabled={isClearAllDisabled || isSingleFilterSelected}
            />
          }
        />
      ) : null}
      {tags.length ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-tags`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          icon={tagSvg}
          label="Tags"
          theme={SectionButtonTheme.SOLID}
          selected={categorizedTags}
          onClick={onClick}
          areChipsAutoCollapsed={isCollapsed.tags}
          onRemoveChip={onRemoveTagChip}
          isRemoveChipDisabled={isSingleFilterSelected}
          customMenuContent={
            <ObjectiveSelectionMenu
              isDisabled={isDisabled || isLineReadOnly || areEllipsisMenusDisabled}
              isCollapsed={isCollapsed.tags}
              isCollapseDisabled={tags.length === 1}
              onClear={clearTags}
              onCollapseOrExpand={() => setIsCollapsed({ ...isCollapsed, tags: !isCollapsed.tags })}
              onExplode={() => {
                onExplode?.(objective, 'tags');
                setIsCollapsed({ ...isCollapsed, tags: false });
              }}
              isExplodeDisabled={tags.length === 1}
              isClearAllDisabled={isClearAllDisabled || isSingleFilterSelected}
            />
          }
        />
      ) : null}
      {routeFrameCodes.length || listFiles.length || mapRouteFrameCodes?.length ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-list`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          label="Frame List"
          onClick={onClick}
          icon={routeFrameListSvg}
          theme={SectionButtonTheme.SOLID}
          selected={selectedView}
          areChipsAutoCollapsed={generatedSelectedAndUploaded?.length >= 10}
          onRemoveChip={onRemoveFrameListChip}
          isRemoveChipDisabled={isSingleFilterSelected}
          customMenuContent={
            <ObjectiveSelectionMenu
              isDisabled={isDisabled || isLineReadOnly || areEllipsisMenusDisabled}
              isCollapsed={isCollapsed.frameList}
              isCollapseDisabled={frameList.routeFrameCodes.length === 1 || frameList.listFiles.length === 1}
              onClear={clearFrameList}
              onCollapseOrExpand={() => setIsCollapsed({ ...isCollapsed, frameList: !isCollapsed.frameList })}
              onExplode={() => {
                onExplode?.(objective, 'frameList');
                setIsCollapsed({ ...isCollapsed, frameList: false });
              }}
              isExplodeDisabled={frameList.routeFrameCodes.length <= 1 && frameList.listFiles.length <= 1}
              isClearAllDisabled={isClearAllDisabled || isSingleFilterSelected}
            />
          }
        />
      ) : null}
      {poi.length ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-poi`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          label="POI"
          onClick={onClick}
          icon={poisSvg}
          theme={SectionButtonTheme.SOLID}
          collapsedExcludedNumber={collapsedExcludedPoiNumber}
          collapsedIncludedNumber={collapsedIncludedPoiNumber}
          onShowMoreMenuClick={onShowMoreMenuClick}
          onRemoveChip={onRemovePOIChip}
          areChipsAutoCollapsed={isCollapsed.poi}
          isRemoveChipDisabled={isSingleFilterSelected}
          customMenuContent={
            <ObjectiveSelectionMenu
              isDisabled={isDisabled || isLineReadOnly || areEllipsisMenusDisabled}
              isCollapsed={isCollapsed.poi}
              isCollapseDisabled={poi.length === 1}
              onClear={clearPOI}
              onCollapseOrExpand={() => setIsCollapsed({ ...isCollapsed, poi: !isCollapsed.poi })}
              onExplode={() => {
                onExplode?.(objective, 'poi');
                setIsCollapsed({ ...isCollapsed, poi: false });
              }}
              isExplodeDisabled={poi.length === 1}
              isClearAllDisabled={isClearAllDisabled || isSingleFilterSelected}
            />
          }
        >
          <POIChipList
            selectedPOIByParentCategory={categorizedPoi}
            onRemovePoiChip={onRemovePOIChip}
            isDisabled={isSingleFilterSelected || isDisabled || isLineReadOnly}
          />
        </SectionButton>
      ) : null}
      <SectionDivider spaceY={1} />
      {budget.value ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-budget`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          icon={moneySvg}
          label={TargetLabel.BUDGET}
          theme={SectionButtonTheme.SOLID}
          onClick={onClick}
          onClear={clearBudget}
          onRemoveChip={clearBudget}
          onShowMoreMenuClick={onShowMoreMenuClick}
          isClearAllDisabled={isClearAllDisabled || isSingleTargetSelected}
          isRemoveChipDisabled={isSingleTargetSelected}
        >
          <Chip
            dataTestId={`${objectiveGroupId}-budget-button-chip`}
            label={formatCurrency({ value: budget.value, localeCode, currencyCode })}
            color={getChipColor(budgetAllocation.status)}
            subLabel={
              budgetAllocation.allocated
                ? formatCurrency({ value: budgetAllocation.allocated, localeCode, currencyCode })
                : undefined
            }
            hasIndicator={!!budgetAllocation.status}
            onRemove={clearBudget}
            isDisabled={isSingleTargetSelected || isDisabled || isLineReadOnly}
          />
        </SectionButton>
      ) : null}
      {frames.value ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-frames`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          icon={framesSvg}
          label={TargetLabel.FRAMES}
          theme={SectionButtonTheme.SOLID}
          onClick={onClick}
          onClear={clearFrames}
          onRemoveChip={clearFrames}
          onShowMoreMenuClick={onShowMoreMenuClick}
          isClearAllDisabled={isClearAllDisabled || isSingleTargetSelected}
          isRemoveChipDisabled={isSingleTargetSelected}
        >
          <Chip
            dataTestId={`${objectiveGroupId}-frames-button-chip`}
            label={formatNumber({ value: frames.value, localeCode })}
            color={getChipColor(framesAllocation.status)}
            subLabel={getSubLabel(framesAllocation.allocated)}
            hasIndicator={!!framesAllocation.status}
            onRemove={clearFrames}
            isDisabled={isSingleTargetSelected || isDisabled || isLineReadOnly}
          />
        </SectionButton>
      ) : null}
      {impressions.value ? (
        <SectionButton
          dataTestId={`section-button-objective-${objectiveGroupId}-impressions`}
          isDisabled={isDisabled || isLineReadOnly}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          icon={eyeSvg}
          label={TargetLabel.IMPRESSIONS}
          theme={SectionButtonTheme.SOLID}
          onClick={onClick}
          onClear={clearImpressions}
          onRemoveChip={clearImpressions}
          onShowMoreMenuClick={onShowMoreMenuClick}
          isClearAllDisabled={isClearAllDisabled || isSingleTargetSelected}
          isRemoveChipDisabled={isSingleTargetSelected}
        >
          <Chip
            dataTestId={`${objectiveGroupId}-impressions-button-chip`}
            label={formatNumber({ value: impressions.value, localeCode })}
            color={getChipColor(impressionsAllocation.status)}
            subLabel={getSubLabel(impressionsAllocation.allocated)}
            hasIndicator={!!impressionsAllocation.status}
            onRemove={clearImpressions}
            isDisabled={isSingleTargetSelected || isDisabled || isLineReadOnly}
          />
        </SectionButton>
      ) : null}
    </div>
  );
};

export default ObjectiveSelection;
